import { Order } from '@bookly-shared/core';
import { RoomServicesModel } from '@bookly-shared/rooms';

export interface RoomBookingsApiResponseModel {
  createTime: string;
  id: number;
  roomDescription: string;
  roomDexId: string;
  roomName: string;
  siteName: string;
  buildingName: string;
  floorName: string;
  startTime: string;
  endTime: string;
  eventName: string;
  status: BookingStatusApiResponseEnum;
  isReapproved?: boolean;
  requester: Requester;
  onBehalfParticipants?: Requester[];
  services: {
    id: number;
    serviceType: RoomServicesApiResponseEnum;
  }[];
}

export interface BookingRequestDetailsApiResponseModel {
  id: number;
  eventName: string;
  numberOfPeople: number;
  wbsCode: string;
  costCenter: string;
  roomLayoutUUID: string;
  userEmail: string;
  startTime: string;
  endTime: string;
  createTime: string;
  status: BookingStatusApiResponseEnum;
  isReapproved?: boolean;
  requester: Requester;
  onBehalfParticipants?: Requester[];
  services: Service[];
  roomInfo: RoomInfo;
  layout?: {
    capacityMax: number;
    capacityMin: number;
    imageUrl: string;
    layoutSelectedTimeDate: string;
    roomLayoutName: string;
    setupAfterMinutes: number;
    setupBeforeMinutes: number;
  };
  generalQuestions: {
    cecOnSite: boolean;
    hcpOnSite: boolean;
    moreThan20Externals: boolean;
  };
  customSetupTimeBefore: number | null;
  customSetupTimeAfter: number | null;
  additionalInfo: string | null;
}

export interface Requester {
  email: string;
  fullName: string;
  photo?: string;
}

export interface Service {
  id: number;
  serviceType: RoomServicesApiResponseEnum;
  url: string;
  ticketName?: string;
  ticketStatus?: string;
}

export interface RoomInfo {
  id: number;
  calendarId: string;
  roomDexId: string;
  roomName: string;
  roomNumber: string;
  roomType: string;
  active: boolean;
  description: string;
  capacity: number;
  manageSpace: boolean;
  automaticApproval: boolean;
  siteName: string;
  buildingName: string;
  buildingNumber: string;
  street: string;
  floorCode: string;
  siteTimeZone: string;
  siteTimeZoneOffset: string;
  photoUrl: string;
}

export interface BookingRequestCreateApiRequestModel
  extends BookingRequestBaseApiModel {
  roomDexId: string;
}

export interface BookingRequestUpdateApiRequestModel
  extends BookingRequestBaseApiModel {
  id: number;
}

export interface BookingRequestBaseApiModel {
  eventName: string;
  numberOfPeople: number;
  wbsCode: string | null;
  costCenter: string | null;
  roomLayoutUUID: string | null;
  startTime: string;
  endTime: string;
  services: {
    serviceType: RoomServicesApiResponseEnum;
  }[];
  onBehalfEmails: string[];
  generalQuestions: {
    cecOnSite: boolean;
    hcpOnSite: boolean;
    moreThan20Externals: boolean;
  };
  customSetupTimeBefore: number | null;
  customSetupTimeAfter: number | null;
  additionalInfo: string | null;
}

export interface ChatMessageApiResponseModel {
  bookingId: number;
  messageId: string;
  createdAt: string;
  sender: {
    email: string;
    fullName: string;
    department: string;
    photo: string;
    rocheLoginId: string;
  };
  recipients: string[];
  readBy: string[];
  readByViewer: boolean;
  message: string;
  type: ChatMessageTypeApiResponseEnum;
}

export interface ChatMessageApiRequestModel {
  bookingId: number;
  message: string;
}

export enum ChatMessageTypeApiResponseEnum {
  Chat = 'CHAT',
  WorkNote = 'WORK_NOTE',
}

export enum RoomServicesApiResponseEnum {
  Tem = 'TEM',
  Catering = 'CATERING',
  Furniture = 'FURNITURE',
  Locker = 'LOCKER',
}

export enum BookingStatusApiResponseEnum {
  InProgress = 'IN_PROGRESS',
  Rejected = 'REJECTED',
  Canceled = 'CANCELED',
  Approved = 'APPROVED',
  Deleted = 'DELETED',
  New = 'NEW',
}

export interface RoomBookingStatusUpdateApiRequestModel {
  id: number;
  message: string;
}

export interface BookingRequestRedirectApiModel {
  bookingId: number;
  roomDexId: string;
  roomBookingRedirectType: RoomBookingRedirectType;
}

export enum RoomBookingRedirectType {
  ROOM_HOST_PANEL = 'ROOM_HOST_PANEL',
  SERVICE_PROVIDER_PANEL = 'SERVICE_PROVIDER_PANEL',
  MY_BOOKING_DETAILS = 'MY_BOOKING_DETAILS',
  UNAUTHORIZED = 'UNAUTHORIZED',
}

export interface BookingRequestPageParams {
  page: number;
  pageSize: number;
  sortBy: BookingRequestSortField;
  sortDirection: Order;
  searchPhrase?: string;
  status?: BookingStatusApiResponseEnum;
  services?: RoomServicesModel[];
  siteNames?: string[];
  buildingNames?: string[];
  floorNames?: string[];
}

export const BOOKING_REQUEST_SORT_FIELDS = ['id', 'startTime'] as const;

export type BookingRequestSortField =
  (typeof BOOKING_REQUEST_SORT_FIELDS)[number];

export interface BookingHistoryParticipantApiResponseModel {
  email?: string;
  fullName?: string;
}

/**
 * A change leaf holds either a scalar (serialised as a string by the API) or a
 * list of participants — `onBehalfParticipants` is sent as a whole array on
 * both sides of the change instead of per-index entries.
 */
export type BookingHistoryChangeValueApiResponseModel =
  | string
  | BookingHistoryParticipantApiResponseModel[];

export interface BookingHistoryChangeApiResponseModel {
  previous?: BookingHistoryChangeValueApiResponseModel;
  current?: BookingHistoryChangeValueApiResponseModel;
}

export interface BookingHistoryChangesApiResponseModel {
  [key: string]:
    | BookingHistoryChangeApiResponseModel
    | BookingHistoryChangesApiResponseModel;
}

export interface BookingHistoryRevisionApiResponseModel {
  revisionNumber: number;
  updatedBy: string;
  updateDate: string;
  bookingStatus: BookingStatusApiResponseEnum;
  isReapproved?: boolean;
  changes: BookingHistoryChangesApiResponseModel;
}

export interface BookingHistoryApiResponseModel {
  initialRoomBooking: BookingRequestDetailsApiResponseModel;
  revisions?: BookingHistoryRevisionApiResponseModel[];
}

/** @deprecated Use {@link BookingHistoryRevisionApiResponseModel} */
export type BookingHistoryEntryApiResponseModel =
  BookingHistoryRevisionApiResponseModel;
